import { CommonModule } from '@angular/common';
import {
	AfterViewInit,
	afterNextRender,
	ChangeDetectionStrategy,
	Component,
	computed,
	effect,
	ElementRef,
	inject,
	Injector,
	input,
	OnDestroy,
	output,
	signal,
	untracked,
	viewChild,
	ViewEncapsulation,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { CardError } from '../../../models/card-model';
import { CardRuntimeRefreshStateService } from '../../../services/card-runtime-refresh-state.service';
import { PageFilterService } from '../../../services/page-filter.service';
import { Grid } from '@highcharts/grid-pro';
import { TableData, type TableResult } from '../../../models/chart-data.model';
import {
	TableCardConfig,
	TableVisualizationConfig,
} from '../../../models/table-config.model';
import { JeptoBaseCardComponent } from '../jepto-base-card.component';
import { openCardErrorDetailsDialog } from '../../../utils/card-error-display.util';
import { tableCardUsesRemoteFetch } from './jepto-table-card-remote-fetch.util';
import {
	JeptoTableCardDefaults,
	buildPaginationConfig,
	TABLE_PAGINATION_PAGE_INFO,
	tablePaginationTotalPages,
} from './jepto-table-card.defaults';
import { JeptoUiProgressBarIndeterminateComponent } from '../../../../ui-lib/buttons-indicators/jepto-ui-progress-bar-indeterminate/jepto-ui-progress-bar-indeterminate.component';
import { parseChartVisualizationInput } from '../../../utils/parse-chart-visualization-input.util';
import {
	normalizeTableGridColumnWidths,
	mergeVisualizationColumnsById,
	configuredTableDataColumnFields,
	configuredTableFieldsOrderFingerprint,
	ensureConfiguredTableColumnDefs,
	tableColumnLabelsByIdFromCardData,
	tableFieldNameByColumnIdFromCardData,
	filterTableColumnarDataToConfiguredFields,
	tableCardHasConfiguredDisplayColumns,
	type TableColumnarData,
} from '../../../utils/table-column.util';
import {
	renderingPresentationFromVisualization,
	tablePageSizeFromConfig,
} from '../../../utils/table-presentation-visualization.util';
import { tableColumnDefsToGridColumns } from '../../../utils/table-grid-column.util';
import { resolveGridColumnRenderCells } from '../../../utils/table-column-render.util';
import {
	gridColumnsHeaderFingerprint,
	gridColumnsCellsFingerprint,
	gridColumnsOrderFingerprint,
	normalizeTableGridColumnHeaderLabels,
	type TableLabelSchemaContext,
} from '../../../utils/table-column-display.util';
import {
	hasRenderableTablePayload,
	isSettledEmptyTablePayload,
	tableDataPayloadFingerprint,
} from '../../editors/jepto-table-editor/services/table-preview-data.util';
import { JeptoCardQueryStatusComponent } from '../jepto-card-query-status/jepto-card-query-status.component';
import { CardRuntimeDataRefreshBroker } from '../../../services/card-runtime-data-refresh.broker';
import { error as logError } from '@jepto/shared/utils/logger';
import {
	TableRemoteFetchBridge,
	extractPageFromColumnarData,
	resolveTotalRowCount,
} from '../../../utils/table-remote-fetch.bridge';
import { TableCardInteractionService } from '../../../services/table-card-interaction.service';
import { groupedCardDate } from '../../../utils/date-bucket-label.util';

const DEFAULT_TABLE_SORTING_ENABLED = true;

// fallow-ignore-next-line unused-type
export interface TableRowClickEvent {
	rowIndex: number;
	columnId: string;
	cardId?: string;
}

// fallow-ignore-next-line unused-type
export interface TableCellClickEvent {
	rowIndex: number;
	column: string;
	value: any;
	cardId?: string;
}

export interface TableColumnResizeEvent {
	/** Grid column index at resize time (display only). */
	columnIndex: number;
	/** Persisted on `visualization.columns` by this id. */
	columnId: string;
	width: number | string;
	cardId?: string;
}

/** Highcharts Grid instance from {@link Grid.grid}. */
type HighchartsGridInstance = {
	destroy?: () => void;
	update?: (
		options?: Record<string, unknown>,
		redraw?: boolean,
		oneToOne?: boolean
	) => Promise<void>;
	data?: {
		provider?: {
			applyQuery?: () => Promise<void>;
		};
	};
	viewport?: {
		reflow?: () => void;
		resizeObserver?: { disconnect?: () => void };
	};
};

/** Grid Pro sets private `isRendered` after the constructor `render()` finishes. */
function gridProIsRendered(g: HighchartsGridInstance): boolean {
	return (g as unknown as { isRendered?: boolean }).isRendered === true;
}

type HighchartsGridCellClickEvent = {
	row?: { index?: number };
	column?: { id?: string };
};

/**
 * Table Card — Highcharts Grid Pro remote provider (`fetchCallback`) for server slices.
 */
@Component({
	selector: 'jepto-table-card',
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: true,
	imports: [
		CommonModule,
		MatIconModule,
		MatMenuModule,
		JeptoUiProgressBarIndeterminateComponent,
		JeptoCardQueryStatusComponent,
	],
	templateUrl: './jepto-table-card.component.html',
	styleUrls: ['./jepto-table-card.component.scss'],
	encapsulation: ViewEncapsulation.None,
})
export class JeptoTableCardComponent
	extends JeptoBaseCardComponent
	implements AfterViewInit, OnDestroy
{
	// ═══════════════════════════════════════════════════════════════════════════
	// CARD-SPECIFIC INPUTS
	// ═══════════════════════════════════════════════════════════════════════════

	config = input<TableCardConfig>();
	/** Standalone `[tableData]`; `[data]` is a bad bind target on custom elements (native `data`). */
	tableData = input<TableData>();
	/** Optional layout override (Storybook); connected mode uses config + defaults. */
	height = input<string>(JeptoTableCardDefaults.styling.height);
	width = input<string>(JeptoTableCardDefaults.styling.width);

	/**
	 * Editor preview: show top progress bar while new data is fetched (metrics/dimensions/date).
	 * Keeps existing table visible underneath, matching {@link JeptoChartCardComponent}.
	 */
	isPreviewDataLoading = input<boolean>(false);

	/**
	 * Editor preview: show empty state instead of blocking loader when the card
	 * has no query data yet (matches {@link JeptoChartCardComponent.allowEmptyChart}).
	 */
	allowEmptyTable = input<boolean>(false);

	/** Optional analytics schema for human-readable headers/cells (editor preview). */
	labelSchema = input<TableLabelSchemaContext>();

	/** Test ID for component targeting in tests (defaults to cardId for uniqueness) */
	testId = input<string>();

	rowClick = output<TableRowClickEvent>();
	cellClick = output<TableCellClickEvent>();
	columnResizeChange = output<TableColumnResizeEvent>();

	private tableContainer = viewChild<ElementRef>('tableContainer');
	private readonly remoteFetchBridge = inject(TableRemoteFetchBridge);
	private readonly tableInteraction = inject(TableCardInteractionService);
	private readonly cardRuntimeRefreshState = inject(
		CardRuntimeRefreshStateService,
		{ optional: true }
	);
	private readonly refreshBroker = inject(CardRuntimeDataRefreshBroker, {
		optional: true,
	});
	private readonly pageFilterService = inject(PageFilterService);
	private dialog = inject(MatDialog);
	private injector = inject(Injector);

	private lastDataFingerprint = '';
	/** Includes page filter query so Grid refetches when search/dropdown filters change. */
	private lastProviderSyncFingerprint = '';
	/** Last rendered grid column count — re-init when it changes (editor 1→N column preview). */
	private lastGridColumnCount = 0;
	/** Last resolved header fingerprint — re-init when humanized labels change (schema load). */
	private lastGridHeaderFingerprint = '';
	/** Last resolved cells fingerprint — re-init when display format changes. */
	private lastGridCellsFingerprint = '';
	/** Last rendered grid column id order — re-init when dimensions/metrics reorder. */
	private lastGridOrderFingerprint = '';
	/** Config dimension/metric order — re-init even when columnDefs lag behind Data-tab reorder. */
	private lastConfiguredFieldsOrderFingerprint = '';
	/** Ignores stale afterNextRender callbacks from overlapping sync effect runs. */
	private gridSyncGeneration = 0;
	/** Serializes Grid `update()` calls so concurrent redraws do not interleave (duplicate pagination). */
	#gridUpdateChain: Promise<void> = Promise.resolve();
	/** In-flight remote `fetchCallback` invocations for this card instance. */
	#gridProviderFetchInflight = 0;
	/**
	 * Set in `ngOnDestroy`. Deferred destroy/update work on `#gridUpdateChain`
	 * can resolve seconds later (remote idle, in-flight `fetchCallback`) and must
	 * not re-create a Grid on a component that is already gone.
	 */
	#componentDestroyed = false;
	/** Resolves when each Grid instance's constructor `render()` has finished (`isRendered`). */
	#initialRenderGates = new WeakMap<HighchartsGridInstance, Promise<void>>();
	/** Re-init when wrap/nowrap row mode changes (Grid Pro sets tbody class only at construct). */
	private lastGridRowWrapFingerprint = '';

	private Grid = Grid;
	private grid?: HighchartsGridInstance;
	private containerResizeObserver?: ResizeObserver;
	private containerResizeReflowTimer:
		| ReturnType<typeof setTimeout>
		| undefined;

	/** True after ngAfterViewInit — drives loading UI. */
	private initialized = signal(false);

	/**
	 * Once the card has painted real rows, never unmount the grid for a transient
	 * empty payload. Unmounting during paging re-inits Highcharts at offset 0 and
	 * flashes a blank card behind the top progress bar.
	 */
	private readonly hasRenderedTableData = signal(false);

	/** While true, skip visualization-driven `grid.update` so column drag is not reset. */
	private readonly columnResizeActive = signal(false);

	private pendingColumnResize: TableColumnResizeEvent | null = null;

	readonly #columnResizePointerUpHandler = (): void => {
		this.#flushPendingColumnResize();
	};

	protected readonly effectiveConfig = computed(() => {
		const direct = this.config();
		if (direct) return direct;
		return this.baseConfig() as TableCardConfig | null;
	});

	/** Computed testId that defaults to cardId when available */
	protected effectiveTestId = computed(
		() =>
			this.testId() ??
			this.effectiveConfig()?.cardId ??
			this.cardId() ??
			'table-card'
	);

	protected readonly effectiveData = computed(() => {
		const direct = this.tableData();
		if (direct) {
			return direct;
		}
		const id = this.cardId() ?? this.effectiveConfig()?.cardId;
		if (!id) {
			return undefined;
		}
		return this.remoteFetchBridge.resolveTableData(id);
	});

	protected readonly parsedVisualization = computed(() => {
		const raw = this.effectiveConfig()?.visualization;
		const parsed = parseChartVisualizationInput(raw);
		if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
			return undefined;
		}
		return parsed as TableVisualizationConfig;
	});

	protected override cardConfigForChrome = computed(() =>
		this.effectiveConfig()
	);

	protected readonly effectivePagination = computed(() => {
		const config = this.effectiveConfig() as TableCardConfig | undefined;
		const viz = this.parsedVisualization();
		const vizPagination =
			viz?.pagination &&
			typeof viz.pagination === 'object' &&
			!Array.isArray(viz.pagination)
				? (viz.pagination as Record<string, unknown>)
				: undefined;
		const featuresPagination = config?.features?.pagination;
		const enabled =
			typeof vizPagination?.['enabled'] === 'boolean'
				? (vizPagination['enabled'] as boolean)
				: (featuresPagination?.enabled ??
					JeptoTableCardDefaults.pagination.enabled);
		return {
			enabled,
			pageSize: config
				? tablePageSizeFromConfig(config)
				: JeptoTableCardDefaults.pagination.pageSize,
		};
	});

	/** When true, cells may wrap within column bounds (period comparison always wraps). */
	protected readonly wrapCellTextEnabled = computed(() => {
		const config = this.effectiveConfig();
		if (config?.data?.comparison?.enabled === true) {
			return true;
		}
		return renderingPresentationFromVisualization(config?.visualization)
			.wrapCellText;
	});

	protected readonly effectiveSortingEnabled = computed(() => {
		const viz = this.parsedVisualization();
		const vizSorting =
			viz?.sorting &&
			typeof viz.sorting === 'object' &&
			!Array.isArray(viz.sorting)
				? (viz.sorting as Record<string, unknown>)
				: undefined;
		if (typeof vizSorting?.['enabled'] === 'boolean') {
			return vizSorting['enabled'] as boolean;
		}
		return (
			this.effectiveConfig()?.features?.sorting?.enabled ??
			DEFAULT_TABLE_SORTING_ENABLED
		);
	});

	/** Column slice + defs for bindings and callbacks (from `effectiveData`, not the `tableData` input alone). */
	protected readonly tableGridSlice = computed(() => {
		const config = this.effectiveConfig();
		const emptySlice = {
			columns: {} as Record<string, unknown[]>,
			columnDefs: [] as NonNullable<TableResult['columnDefs']>,
		};
		if (config && !tableCardHasConfiguredDisplayColumns(config)) {
			return emptySlice;
		}
		const raw = this.effectiveData();
		const result = raw?.data?.result;
		if (!result?.columns || Object.keys(result.columns).length === 0) {
			return emptySlice;
		}
		const defs =
			result.columnDefs && result.columnDefs.length > 0
				? result.columnDefs
				: [];
		const columnarKeys = Object.keys(result.columns);
		const scopedDefs = config
			? ensureConfiguredTableColumnDefs(config, defs, columnarKeys)
			: defs;
		const scopedColumns = config
			? filterTableColumnarDataToConfiguredFields(
					config,
					result.columns as TableColumnarData
				)
			: (result.columns as Record<string, unknown[]>);
		return {
			columns: scopedColumns as Record<string, unknown[]>,
			columnDefs: scopedDefs,
		};
	});

	/** Hide table body until config and renderable data exist (initial load only). */
	protected readonly isBlockingLoad = computed(() => {
		const cfg = this.effectiveConfig();
		if (!cfg) {
			return true;
		}
		if (this.allowEmptyTable()) {
			return false;
		}
		// Keep the grid mounted after first paint and during Grid paging waits.
		if (this.hasRenderedTableData()) {
			return false;
		}
		const id = this.cardId() ?? cfg.cardId;
		if (id && this.tableInteraction.isPagingLoading(id)) {
			return false;
		}
		return !hasRenderableTablePayload(this.effectiveData());
	});

	private readonly effectiveCardId = computed(
		() => this.cardId() ?? this.effectiveConfig()?.cardId
	);

	/** Report canvas refresh (date/filter change) from {@link CardRuntimeRefreshStateService}. */
	private readonly isReportCanvasRefreshing = computed(() => {
		const id = this.effectiveCardId();
		if (!id || !this.cardRuntimeRefreshState) {
			return false;
		}
		return this.cardRuntimeRefreshState.loadingCardIds().has(id);
	});

	/** Top progress bar — preview/canvas/paging, or blocking load (not idle create empty). */
	protected readonly showTopProgressBar = computed(() => {
		const id = this.effectiveCardId();
		const pagingLoading = !!id && this.tableInteraction.isPagingLoading(id);
		if (
			this.isPreviewDataLoading() ||
			this.isReportCanvasRefreshing() ||
			pagingLoading
		) {
			return true;
		}
		if (this.showCreateTableEmptyState()) {
			return false;
		}
		return (
			this.isBlockingLoad() ||
			(!this.allowEmptyTable() && !this.initialized())
		);
	});

	/** When true, show placeholder — no defs/columns in the resolved slice yet. */
	readonly showTableEmptyPlaceholder = computed(() => {
		if (hasRenderableTablePayload(this.effectiveData())) {
			return false;
		}
		const slice = this.tableGridSlice();
		return (
			slice.columnDefs.length === 0 &&
			Object.keys(slice.columns).length === 0
		);
	});

	/** Editor create flow: instructional empty state (matches chart card draft preview). */
	protected readonly showCreateTableEmptyState = computed(
		() => this.allowEmptyTable() && this.showTableEmptyPlaceholder()
	);

	/** Page preview: query finished with no rows. */
	protected readonly showPageQueryEmptyState = computed(
		() =>
			!this.isReportCanvasRefreshing() &&
			!this.allowEmptyTable() &&
			!this.hasError() &&
			!this.showCreateTableEmptyState() &&
			(isSettledEmptyTablePayload(this.effectiveData()) ||
				(!!this.effectiveData() && this.showTableEmptyPlaceholder()))
	);

	protected override configCardErrorSource = computed(
		() => this.effectiveConfig()?.error
	);

	protected override dataCardErrorSource = computed(
		() =>
			(this.effectiveData() as { error?: CardError } | undefined)?.error
	);

	constructor() {
		super();

		effect(() => {
			if (hasRenderableTablePayload(this.effectiveData())) {
				this.hasRenderedTableData.set(true);
			}
		});

		/** Mount grid when column defs arrive (including after WebSocket data lands post-view init). */
		effect(() => {
			this.labelSchema();
			if (!this.initialized()) return;
			if (this.grid) return;
			if (!this.#hasGridInitReadyData()) return;

			afterNextRender(
				() => {
					this.#initializeTableIfReady();
				},
				{ injector: this.injector }
			);
		});

		/**
		 * Page-query empty state swaps the grid container for status UI. Destroy the
		 * orphaned Grid instance and remount when rows return after filter clear.
		 */
		effect(() => {
			if (!this.initialized()) return;
			const queryEmpty = this.showPageQueryEmptyState();
			const container = this.tableContainer();
			// Re-run when WS payload arrives after empty state clears.
			this.effectiveData();

			if (queryEmpty) {
				if (this.grid) {
					afterNextRender(
						() => {
							untracked(() => {
								if (!this.showPageQueryEmptyState()) {
									return;
								}
								this.#enqueueGridDestroy(
									() => {
										this.lastProviderSyncFingerprint = '';
										this.lastDataFingerprint = '';
									},
									() => this.showPageQueryEmptyState()
								);
							});
						},
						{ injector: this.injector }
					);
				}
				return;
			}

			if (!this.grid && container && this.#hasGridInitReadyData()) {
				afterNextRender(
					() => {
						this.#initializeTableIfReady();
					},
					{ injector: this.injector }
				);
			}
		});

		/** Host refetch (global date, page filters, WebSocket): invalidate remote provider query. */
		effect(() => {
			if (!this.initialized() || !this.grid) return;
			const id = this.cardId() ?? this.effectiveConfig()?.cardId;
			if (!id) return;
			const data = this.effectiveData();
			const dataFingerprint = tableDataPayloadFingerprint(data);
			const filterFingerprint = JSON.stringify(
				this.pageFilterService.pageFiltersForQuery()
			);
			const wasRefreshing = this.isReportCanvasRefreshing();
			const fingerprint = `${dataFingerprint}|${filterFingerprint}|${wasRefreshing}`;
			if (!fingerprint || fingerprint === this.lastProviderSyncFingerprint) {
				return;
			}
			const isFirstSlice = !this.lastProviderSyncFingerprint;
			this.lastProviderSyncFingerprint = fingerprint;
			this.lastDataFingerprint = dataFingerprint;
			if (isFirstSlice && !hasRenderableTablePayload(data)) {
				return;
			}
			if (wasRefreshing) {
				return;
			}
			// Grid paging/sort already drives fetchCallback; applyQuery here aborts it
			// and Highcharts collapses pagination to "Showing 0–0 of 0".
			if (this.#shouldSkipGridProviderSync(id)) {
				return;
			}
			afterNextRender(
				() => {
					void this.grid?.data?.provider?.applyQuery?.();
				},
				{ injector: this.injector }
			);
		});

		effect(() => {
			this.themeMode();
			void this.themeService.getActiveThemeModeSignal()();
			if (this.initialized() && this.grid) this.applyTheme();
		});

		/** Sync grid columns when `columnDefs`, config fields, visualization overrides, or label schema change. */
		effect(() => {
			this.parsedVisualization();
			this.wrapCellTextEnabled();
			this.labelSchema();
			const config = this.effectiveConfig();
			const hasDisplayColumns =
				config != null && tableCardHasConfiguredDisplayColumns(config);
			if (this.columnResizeActive()) return;
			if (!this.initialized()) return;

			if (!hasDisplayColumns) {
				if (this.grid) {
					afterNextRender(
						() => {
							this.#enqueueGridDestroy(
								() => {
									this.lastGridColumnCount = 0;
									this.lastGridHeaderFingerprint = '';
									this.lastGridCellsFingerprint = '';
									this.lastGridOrderFingerprint = '';
									this.lastConfiguredFieldsOrderFingerprint =
										'';
								},
								() => {
									const cfg = this.effectiveConfig();
									return (
										cfg == null ||
										!tableCardHasConfiguredDisplayColumns(
											cfg
										)
									);
								}
							);
						},
						{ injector: this.injector }
					);
				}
				return;
			}

			if (!this.grid) return;
			const cardId = (this.cardId() ?? config?.cardId ?? '').trim();
			if (this.#shouldSkipGridProviderSync(cardId)) {
				return;
			}
			const defs = this.tableGridSlice().columnDefs;
			if (defs.length === 0) return;
			const mergedColumns = this.buildGridColumns(defs);
			const gridColumnCount = mergedColumns.length;
			const headerFingerprint =
				gridColumnsHeaderFingerprint(mergedColumns);
			const cellsFingerprint = gridColumnsCellsFingerprint(mergedColumns);
			const orderFingerprint = gridColumnsOrderFingerprint(mergedColumns);
			const configuredOrderFingerprint =
				configuredTableFieldsOrderFingerprint(config);
			const rowWrapFingerprint = this.#gridRowWrapFingerprint();
			if (gridColumnCount === 0) return;

			const syncGeneration = ++this.gridSyncGeneration;
			afterNextRender(
				() => {
					if (syncGeneration !== this.gridSyncGeneration) {
						return;
					}
					const structuralChange =
						gridColumnCount !== this.lastGridColumnCount ||
						headerFingerprint !== this.lastGridHeaderFingerprint ||
						cellsFingerprint !== this.lastGridCellsFingerprint ||
						orderFingerprint !== this.lastGridOrderFingerprint ||
						configuredOrderFingerprint !==
							this.lastConfiguredFieldsOrderFingerprint ||
						rowWrapFingerprint !== this.lastGridRowWrapFingerprint;
					// Grid Pro `update()` does not reliably reorder columns — always
					// destroy+re-init when column structure/order/format changes.
					if (structuralChange) {
						this.#enqueueGridDestroy(() => {
							this.initializeTable(defs);
						});
						return;
					}
					const g = this.grid;
					if (!g?.update) return;
					const { events: _events, ...rest } =
						this.buildTableOptions(defs);
					this.#enqueueGridUpdate(async grid => {
						if (!grid.update) return;
						await grid.update(rest, true, true);
						if (syncGeneration !== this.gridSyncGeneration) {
							return;
						}
						if (grid !== this.grid) {
							return;
						}
						const updateCardId = (
							this.cardId() ??
							this.effectiveConfig()?.cardId ??
							''
						).trim();
						if (!this.#shouldSkipGridProviderSync(updateCardId)) {
							void grid.data?.provider?.applyQuery?.();
						}
						this.#scheduleGridViewportReflow();
					});
				},
				{ injector: this.injector }
			);
		});
	}

	#gridRowWrapFingerprint(): string {
		return this.wrapCellTextEnabled() ? 'wrap' : 'nowrap';
	}

	ngAfterViewInit(): void {
		this.initialized.set(true);
		this.#initializeTableIfReady();
	}

	ngOnDestroy(): void {
		document.removeEventListener(
			'mouseup',
			this.#columnResizePointerUpHandler
		);
		this.#componentDestroyed = true;
		this.#enqueueGridDestroy();
	}

	#disconnectGridProInternalResizeObserver(
		grid: HighchartsGridInstance | undefined
	): void {
		if (!grid) {
			return;
		}
		try {
			grid.viewport?.resizeObserver?.disconnect?.();
		} catch {
			/* ignore */
		}
	}

	#destroyGrid(): void {
		this.#disconnectTableContainerResizeObserver();
		this.lastGridColumnCount = 0;
		this.lastGridHeaderFingerprint = '';
		this.lastGridCellsFingerprint = '';
		this.lastGridOrderFingerprint = '';
		this.lastConfiguredFieldsOrderFingerprint = '';
		this.lastGridRowWrapFingerprint = '';
		const el = this.tableContainer()?.nativeElement;
		const grid = this.grid;
		this.#disconnectGridProInternalResizeObserver(grid);
		try {
			grid?.destroy?.();
		} catch {
			/* ignore */
		}
		this.grid = undefined;
		// Drop orphaned pagination nodes left by a stale Grid `Pagination.render()`
		// that raced the constructor's async initial `render()` / `renderViewport()`.
		el?.replaceChildren();
	}

	/**
	 * Runs `run` on the grid update chain so only one `update()`/redraw runs at a
	 * time. Re-binds `this.grid` inside the chain: if a structural change has
	 * destroyed/replaced the grid while this update was queued, the run no-ops.
	 * Waits for Grid Pro's initial constructor `render()` (`isRendered`) before
	 * calling `update()` — that path is not on Grid's `redrawQueue` and racing it
	 * duplicates `.hcg-pagination` controls.
	 * Errors are swallowed so one failed update cannot poison the chain.
	 */
	#enqueueGridUpdate(
		run: (g: HighchartsGridInstance) => Promise<void>
	): void {
		this.#gridUpdateChain = this.#gridUpdateChain
			.then(async () => {
				const g = this.grid;
				if (!g || this.#componentDestroyed) return;
				await this.#waitForInitialGridRender(g);
				if (g !== this.grid || this.#componentDestroyed) return;
				await run(g);
				this.#removeDuplicatePaginationControls();
			})
			.catch(error => {
				logError('[JeptoTableCard] grid update failed', error);
			});
	}

	#cardIdForGridLifecycle(): string {
		return (this.cardId() ?? this.effectiveConfig()?.cardId ?? '').trim();
	}

	async #awaitGridLifecycleIdle(cardId: string): Promise<void> {
		// Do not await `#gridUpdateChain` here — destroy is appended to that chain
		// and would deadlock waiting on itself.
		if (cardId) {
			await this.tableInteraction.awaitGridRemoteIdle(cardId);
		}
		const deadline = Date.now() + 8000;
		while (this.#gridProviderFetchInflight > 0 && Date.now() < deadline) {
			await new Promise(resolve => setTimeout(resolve, 16));
		}
		await new Promise<void>(resolve =>
			requestAnimationFrame(() =>
				requestAnimationFrame(() => resolve())
			)
		);
	}

	/**
	 * Runs destroy after Grid `update()`, remote paging, and `fetchCallback` idle
	 * so Highcharts does not touch `columnPolicy` / pagination on a destroyed grid.
	 */
	#gridIsMountedInTableContainer(): boolean {
		const container = this.tableContainer()?.nativeElement;
		if (!container || !this.grid) {
			return false;
		}
		return container.childElementCount > 0;
	}

	/**
	 * After a deferred destroy is cancelled, drop a detached Grid instance so mount
	 * effects and later inits are not blocked by a stale `this.grid` reference.
	 */
	#reconcileGridAfterCancelledDestroy(): void {
		if (!this.grid) {
			return;
		}
		if (this.#gridIsMountedInTableContainer()) {
			return;
		}
		this.#destroyGrid();
		if (
			!this.showPageQueryEmptyState() &&
			this.#hasGridInitReadyData()
		) {
			afterNextRender(
				() => {
					this.#initializeTableIfReady();
				},
				{ injector: this.injector }
			);
		}
	}

	#enqueueGridDestroy(
		afterDestroy?: () => void,
		shouldDestroy?: () => boolean
	): void {
		this.#gridUpdateChain = this.#gridUpdateChain
			.then(async () => {
				const cardId = this.#cardIdForGridLifecycle();
				await this.#awaitGridLifecycleIdle(cardId);
				// Grid Pro's `destroy()` deletes `columnPolicy`; destroying before the
				// constructor `render()` settles makes it throw (JEP-10672).
				const mounting = this.grid;
				if (mounting) {
					await this.#waitForInitialGridRender(mounting);
				}
				if (this.#componentDestroyed) {
					// Component went away while we waited: tear down the grid but
					// never run `afterDestroy` (re-init / retry) or reconcile.
					this.#destroyGrid();
					return;
				}
				if (shouldDestroy && !shouldDestroy()) {
					this.#reconcileGridAfterCancelledDestroy();
					return;
				}
				this.#destroyGrid();
				afterDestroy?.();
			})
			.catch(error => {
				logError('[JeptoTableCard] grid update failed', error);
			});
	}

	/**
	 * Grid.grid() starts `void this.render()` without awaiting; `isRendered` flips
	 * true only after that completes. Calling `update()` earlier races
	 * `renderViewport()` and can leave two sibling pagination navs.
	 */
	#armInitialRenderGate(g: HighchartsGridInstance): void {
		if (gridProIsRendered(g)) {
			this.#initialRenderGates.set(g, Promise.resolve());
			return;
		}
		this.#initialRenderGates.set(
			g,
			new Promise<void>(resolve => {
				const started = Date.now();
				const id = setInterval(() => {
					if (
						this.grid !== g ||
						gridProIsRendered(g) ||
						Date.now() - started > 5000
					) {
						clearInterval(id);
						resolve();
					}
				}, 16);
			})
		);
	}

	#waitForInitialGridRender(g: HighchartsGridInstance): Promise<void> {
		return this.#initialRenderGates.get(g) ?? Promise.resolve();
	}

	/** Keep a single bottom pagination bar if a race left sibling navs. */
	#removeDuplicatePaginationControls(): void {
		const root = this.tableContainer()?.nativeElement;
		if (!root) return;
		const container =
			(root.querySelector('.hcg-container') as HTMLElement | null) ?? root;
		const pags = container.querySelectorAll(':scope > .hcg-pagination');
		for (let i = 0; i < pags.length - 1; i++) {
			pags[i].remove();
		}
	}

	#reflowGridViewport(): void {
		try {
			this.grid?.viewport?.reflow?.();
		} catch {
			/* ignore */
		}
	}

	/** Flex-fill cards omit inline height; fixed values (e.g. `400px`) still apply. */
	protected containerHeightStyle(): string | null {
		const h = this.height();
		return h === '100%' ? null : h;
	}

	/**
	 * Grid Pro sets `tr.style.width` to the sum of column widths at init. In the
	 * editor modal the container often reaches its final width after the first
	 * layout pass, so rows stay narrower than the table until viewport reflow.
	 */
	#observeTableContainerResize(element: HTMLElement): void {
		this.#disconnectTableContainerResizeObserver();
		if (typeof ResizeObserver === 'undefined') {
			return;
		}
		this.containerResizeObserver = new ResizeObserver(entries => {
			const rect = entries[0]?.contentRect;
			if (!rect || rect.width <= 0 || rect.height <= 0) {
				return;
			}
			if (this.containerResizeReflowTimer !== undefined) {
				clearTimeout(this.containerResizeReflowTimer);
			}
			this.containerResizeReflowTimer = setTimeout(() => {
				this.containerResizeReflowTimer = undefined;
				this.#reflowGridViewport();
			}, 32);
		});
		this.containerResizeObserver.observe(element);
	}

	#disconnectTableContainerResizeObserver(): void {
		this.containerResizeObserver?.disconnect();
		this.containerResizeObserver = undefined;
		if (this.containerResizeReflowTimer !== undefined) {
			clearTimeout(this.containerResizeReflowTimer);
			this.containerResizeReflowTimer = undefined;
		}
	}

	/**
	 * While Grid paging owns the remote provider, skip `grid.update` / `applyQuery`.
	 * WS-driven `effectiveData` updates otherwise rebuild RemoteDataProvider,
	 * reset `currentPage` to 1, and a page-1 fetchCallback can paint a later
	 * slice — "Showing 1–10" with page-2 rows.
	 */
	#shouldSkipGridProviderSync(cardId: string | undefined): boolean {
		const id = cardId?.trim();
		if (!id) {
			return false;
		}
		return (
			this.tableInteraction.shouldSkipApplyQueryForPaging(id) ||
			this.tableInteraction.isPagingLoading(id)
		);
	}

	/** Recalculate column widths after editor modal / preview panel layout settles. */
	#scheduleGridViewportReflow(): void {
		this.#reflowGridViewport();
		requestAnimationFrame(() => {
			this.#reflowGridViewport();
			requestAnimationFrame(() => this.#reflowGridViewport());
		});
		setTimeout(() => this.#reflowGridViewport(), 100);
		setTimeout(() => this.#reflowGridViewport(), 300);
	}

	/** Grid Pro pagination UI from card config (not client-side slicing). */
	private paginationOptions(): Record<string, unknown> | false {
		const pagination = this.effectivePagination();
		if (!pagination.enabled) {
			return false;
		}
		const data = this.effectiveData();
		const totalRows = data ? resolveTotalRowCount(data) : 0;
		const singlePage =
			tablePaginationTotalPages(totalRows, pagination.pageSize) <= 1;
		const built = buildPaginationConfig(
			{
				...JeptoTableCardDefaults.pagination,
				pageSize: pagination.pageSize,
			},
			{ singlePage }
		);
		if (!built) {
			return false;
		}
		return built;
	}

	private initializeTable(
		defs: NonNullable<TableResult['columnDefs']>
	): void {
		const el = this.tableContainer()?.nativeElement;
		if (!el) return;
		try {
			this.grid = this.Grid.grid(
				el,
				this.buildTableOptions(defs)
			) as unknown as HighchartsGridInstance;
			this.#armInitialRenderGate(this.grid);
			document.addEventListener(
				'mouseup',
				this.#columnResizePointerUpHandler
			);
			this.#observeTableContainerResize(el);
			const mergedColumns = this.buildGridColumns(defs);
			this.lastGridColumnCount = mergedColumns.length;
			this.lastGridHeaderFingerprint =
				gridColumnsHeaderFingerprint(mergedColumns);
			this.lastGridCellsFingerprint =
				gridColumnsCellsFingerprint(mergedColumns);
			this.lastGridOrderFingerprint =
				gridColumnsOrderFingerprint(mergedColumns);
			this.lastConfiguredFieldsOrderFingerprint =
				configuredTableFieldsOrderFingerprint(
					this.effectiveConfig() ?? undefined
				);
			this.lastGridRowWrapFingerprint = this.#gridRowWrapFingerprint();
			this.#scheduleGridViewportReflow();
			setTimeout(() => {
				this.applyTheme();
				this.#reflowGridViewport();
				this.#removeDuplicatePaginationControls();
			}, 50);
		} catch (err) {
			logError('[JeptoTableCard] Failed to initialize grid:', err);
		}
	}

	#columnDefsForGridInit(): NonNullable<TableResult['columnDefs']> {
		return this.tableGridSlice().columnDefs;
	}

	#hasGridInitReadyData(): boolean {
		const config = this.effectiveConfig();
		if (config && !tableCardHasConfiguredDisplayColumns(config)) {
			return false;
		}
		return this.#columnDefsForGridInit().length > 0;
	}

	#initializeTableIfReady(): void {
		if (this.grid || !this.#hasGridInitReadyData()) {
			return;
		}
		try {
			this.initializeTable(this.#columnDefsForGridInit());
		} catch (e) {
			logError('[JeptoTableCard] init failed', e);
		}
	}

	/**
	 * Grid Pro invokes `column.options.events` (not `columnDefaults.events`).
	 * @see node_modules/@highcharts/grid-pro/es-modules/Grid/Pro/GridEvents.js
	 */
	private buildColumnOptionEvents(): Record<string, unknown> {
		const self = this;

		return {
			afterResize: function (this: {
				id?: string;
				index?: number;
				getWidth?: () => number;
			}) {
				const columnId = this.id;
				if (!columnId || typeof this.getWidth !== 'function') {
					return;
				}
				const columnIndex =
					typeof this.index === 'number' ? this.index : -1;
				if (columnIndex < 0) {
					return;
				}
				self.columnResizeActive.set(true);
				self.pendingColumnResize = {
					columnIndex,
					columnId,
					width: this.getWidth(),
					cardId: self.cardId() ?? self.effectiveConfig()?.cardId,
				};
			},
		};
	}

	/** Storybook / static `[tableData]` — no `cardId`, no server refetch. */
	#resolveStandaloneFetch(): {
		columns: Record<string, Array<string | number | boolean | null>>;
		totalRowCount: number;
	} {
		const data = this.effectiveData();
		const config = this.effectiveConfig();
		if (
			!data ||
			(config && !tableCardHasConfiguredDisplayColumns(config))
		) {
			return { columns: {}, totalRowCount: 0 };
		}
		return {
			columns: this.remoteFetchBridge.toRemoteColumns(
				data,
				config ?? undefined
			),
			totalRowCount: resolveTotalRowCount(data),
		};
	}

	#buildFetchCallback(): (
		this: unknown,
		querying: unknown,
		offset: number,
		limit: number,
		signal?: AbortSignal
	) => Promise<{
		columns: Record<string, unknown[]>;
		totalRowCount: number;
	}> {
		const self = this;
		return async function (
			_querying: unknown,
			offset: number,
			limit: number,
			signal?: AbortSignal
		) {
			const cardId = (
				self.cardId() ??
				self.effectiveConfig()?.cardId ??
				''
			).trim();
			// Bound `[tableData]` is for display (editor preview); with a real cardId
			// always use remote paging so create/edit modal matches the page canvas.
			// Static/Storybook without cardId: client-slice the bound payload only.
			if (!tableCardUsesRemoteFetch(cardId)) {
				const standalone = self.#resolveStandaloneFetch();
				return {
					columns: extractPageFromColumnarData(
						standalone.columns,
						offset,
						limit
					) as Record<string, unknown[]>,
					totalRowCount: standalone.totalRowCount,
				};
			}
			self.#gridProviderFetchInflight++;
			try {
				const result = await self.remoteFetchBridge.fetchSlice({
					cardId,
					offset,
					limit,
					querying: _querying,
					signal,
					config: self.effectiveConfig() ?? undefined,
				});
				return {
					columns: result.columns as Record<string, unknown[]>,
					totalRowCount: result.totalRowCount,
				};
			} finally {
				await new Promise<void>(resolve =>
					requestAnimationFrame(() =>
						requestAnimationFrame(() => resolve())
					)
				);
				self.#gridProviderFetchInflight--;
			}
		};
	}

	#flushPendingColumnResize(): void {
		const pending = this.pendingColumnResize;
		this.pendingColumnResize = null;
		this.columnResizeActive.set(false);
		if (pending?.columnId) {
			this.columnResizeChange.emit(pending);
		}
	}

	private buildGridColumns(
		columnDefs: NonNullable<TableResult['columnDefs']>
	): Array<Record<string, unknown>> {
		const visualization = this.parsedVisualization();
		const config = this.effectiveConfig();
		if (columnDefs.length === 0) {
			return [];
		}

		const columnarKeys = Object.keys(this.tableGridSlice().columns);
		const scopedColumnDefs = config
			? ensureConfiguredTableColumnDefs(config, columnDefs, columnarKeys)
			: columnDefs;
		if (scopedColumnDefs.length === 0) {
			return [];
		}

		const columnEvents = this.buildColumnOptionEvents();
		const headerLabelsById = config
			? tableColumnLabelsByIdFromCardData(config)
			: undefined;
		const fieldNameByColumnId = config
			? tableFieldNameByColumnIdFromCardData(config, scopedColumnDefs)
			: undefined;
		const labelSchema = this.labelSchema();
		const comparisonStacked = config?.data?.comparison?.enabled === true;
		const groupedDate = groupedCardDate(config?.data);
		const baseColumns = tableColumnDefsToGridColumns(scopedColumnDefs, {
			sortingEnabled: this.effectiveSortingEnabled(),
			columnEvents,
			headerLabelsById,
			fieldNameByColumnId,
			schema: labelSchema,
			comparisonStacked,
			...(groupedDate ? { groupedDate } : {}),
		});
		const visualizationColumns = Array.isArray(visualization?.columns)
			? (visualization.columns as Array<Record<string, unknown>>)
			: [];
		const configuredFields = config
			? configuredTableDataColumnFields(config)
			: undefined;
		const merged = mergeVisualizationColumnsById(
			baseColumns,
			visualizationColumns,
			configuredFields
		).map(resolveGridColumnRenderCells);
		return normalizeTableGridColumnWidths(
			normalizeTableGridColumnHeaderLabels(merged, {
				fieldNameByColumnId,
				headerLabelsById,
				schema: labelSchema,
			})
		);
	}

	private buildTableOptions(
		columnDefs: NonNullable<TableResult['columnDefs']>
	): Record<string, unknown> {
		const mergedColumns = this.buildGridColumns(columnDefs);
		const visualization = this.parsedVisualization();

		const vizRecord = (visualization ?? {}) as Record<string, unknown>;
		const {
			columns: _ignoredColumns,
			dataTable: _ignoredDataTable,
			data: _ignoredData,
			events: _ignoredEvents,
			// Grid Pro `header` overrides which columns render — never pass saved viz header.
			header: _ignoredHeader,
			...visualizationRest
		} = vizRecord;

		const self = this;
		const vizRest = visualizationRest as Record<string, unknown>;
		const vizColumnDefaults = vizRest['columnDefaults'] as
			| Record<string, unknown>
			| undefined;
		const { events: _columnDefaultEvents, ...columnDefaultsRest } =
			vizColumnDefaults ?? {};

		const rendering = this.#withoutRowPinning(
			this.deepMergeOptions(
				JeptoTableCardDefaults.rendering as Record<string, unknown>,
				vizRest['rendering'] as Record<string, unknown> | undefined
			)
		);

		return {
			...vizRest,
			columnDefaults: {
				...columnDefaultsRest,
				sorting: {
					...(typeof columnDefaultsRest.sorting === 'object' &&
					columnDefaultsRest.sorting !== null &&
					!Array.isArray(columnDefaultsRest.sorting)
						? (columnDefaultsRest.sorting as Record<
								string,
								unknown
							>)
						: {}),
					enabled:
						this.effectiveSortingEnabled() &&
						JeptoTableCardDefaults.columnDefaults.sorting.enabled,
				},
			},
			columns: mergedColumns,
			data: {
				providerType: 'remote',
				autogenerateColumns: false,
				fetchCallback: this.#buildFetchCallback(),
			},
			rendering,
			pagination: this.paginationOptions(),
			lang: {
				pagination: {
					pageInfo: TABLE_PAGINATION_PAGE_INFO,
				},
			},
			credits: JeptoTableCardDefaults.credits,
			events: {
				cell: {
					click: (e: unknown) => {
						if (self.isGridCellClickEvent(e)) {
							self.handleCellClick(e);
						}
					},
				},
			},
		};
	}

	/** Remote `fetchCallback` duplicates pinned rows — strip persisted pinning from viz. */
	#withoutRowPinning(
		rendering: Record<string, unknown>
	): Record<string, unknown> {
		const rows = rendering['rows'];
		if (!rows || typeof rows !== 'object' || Array.isArray(rows)) {
			return rendering;
		}
		const { pinning: _pinning, ...rowsRest } = rows as Record<
			string,
			unknown
		>;
		return { ...rendering, rows: rowsRest };
	}

	private deepMergeOptions(
		base: Record<string, unknown>,
		override?: Record<string, unknown>
	): Record<string, unknown> {
		if (!override) {
			return { ...base };
		}
		const merged: Record<string, unknown> = { ...base };
		for (const key of Object.keys(override)) {
			const overrideValue = override[key];
			const baseValue = merged[key];
			if (
				this.isObjectLike(baseValue) &&
				this.isObjectLike(overrideValue)
			) {
				merged[key] = this.deepMergeOptions(
					baseValue as Record<string, unknown>,
					overrideValue as Record<string, unknown>
				);
				continue;
			}
			merged[key] = overrideValue;
		}
		return merged;
	}

	private isObjectLike(value: unknown): value is Record<string, unknown> {
		return !!value && typeof value === 'object' && !Array.isArray(value);
	}

	private isGridCellClickEvent(
		value: unknown
	): value is HighchartsGridCellClickEvent {
		return this.isObjectLike(value);
	}

	private handleCellClick(event: HighchartsGridCellClickEvent): void {
		const rowIdx = event.row?.index;
		const colId =
			typeof event.column?.id === 'string' ? event.column.id : undefined;

		if (rowIdx !== undefined && colId) {
			const columns = this.tableGridSlice().columns;
			const value = columns[colId]?.[rowIdx as number];
			const cid = this.cardId();
			this.cellClick.emit({
				rowIndex: rowIdx as number,
				column: colId,
				value,
				cardId: cid,
			});
			this.rowClick.emit({
				rowIndex: rowIdx as number,
				columnId: colId,
				cardId: cid,
			});
		}
	}

	private applyTheme(): void {
		const container = this.tableContainer()?.nativeElement;
		const hostEl =
			container?.closest('jepto-table-card') ?? container?.parentElement;

		let activeTheme: 'light' | 'dark' = 'light';
		const mode = this.themeMode();
		if (mode === 'light' || mode === 'dark') {
			activeTheme = mode;
		} else {
			// `getActiveThemeModeSignal()` resolves `auto` to light or dark already
			activeTheme = this.themeService.getActiveThemeModeSignal()();
		}

		container?.classList.remove(
			'theme-light',
			'theme-dark',
			'highcharts-light',
			'highcharts-dark'
		);
		container?.classList.add(
			`theme-${activeTheme}`,
			`highcharts-${activeTheme}`
		);

		if (hostEl) {
			hostEl.classList.remove(
				'theme-light',
				'theme-dark',
				'highcharts-light',
				'highcharts-dark'
			);
			hostEl.classList.add(
				`theme-${activeTheme}`,
				`highcharts-${activeTheme}`
			);
		}
	}

	onRetry(): void {
		this.#enqueueGridDestroy(() => {
			this.lastDataFingerprint = '';
			this.lastProviderSyncFingerprint = '';
			const config = this.effectiveConfig();
			const id = config?.cardId ?? this.cardId();
			if (id && this.refreshBroker?.hasListeners) {
				this.cardRuntimeRefreshState?.beginCardRefresh(id);
				this.refreshBroker.requestRefresh(id);
				return;
			}
			if (config && !tableCardHasConfiguredDisplayColumns(config)) {
				return;
			}
			const defs = this.#columnDefsForGridInit();
			if (defs.length) {
				this.initializeTable(defs);
			}
		});
	}

	/** Open error details modal */
	openErrorDetails(): void {
		const err = this.cardError();
		if (!err) return;

		openCardErrorDetailsDialog(this.dialog, err, {
			cardId: this.effectiveConfig()?.cardId ?? this.cardId(),
			cardTitle: this.effectiveTitle(),
		});
	}
}
